import React, { useState, useEffect } from 'react';
import LORManagerSelector from './LORManagerSelector';
import Icon from '../../components/Icon';

const GRADE_COLORS = {
  elite:   '#22c55e',
  good:    '#86efac',
  neutral: '#94a3b8',
  tough:   '#f97316',
  brutal:  '#ef4444',
};
const GRADE_LABELS = {
  elite: 'Elite', good: 'Good', neutral: 'Neutral', tough: 'Tough', brutal: 'Brutal',
};

export default function LORStartSit({ api }) {
  const [rosterId, setRosterId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!rosterId) return;
    setLoading(true);
    setError(null);
    fetch(`${api}/api/lor/intel/startsit/${rosterId}`)
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError('Could not load Start/Sit data.'); setLoading(false); });
  }, [api, rosterId]);

  return (
    <div>
      <div className="lor-page-header">
        <h2 className="lor-page-title" style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Icon emoji="⚖️" size={18} /> Start / Sit</h2>
        <LORManagerSelector api={api} onSelect={setRosterId} selectedRosterId={rosterId} />
      </div>

      {!rosterId && (
        <div className="lor-intel-empty">
          <div className="lor-intel-empty-icon"><Icon emoji="⚖️" size={28} /></div>
          <p>Select your team above to see personalized Start/Sit recommendations.</p>
        </div>
      )}

      {rosterId && loading && <div className="lor-loading">Analyzing your lineup…</div>}
      {rosterId && error && <div className="lor-empty">{error}</div>}

      {data && !loading && (
        <>
          <div className="lor-intel-meta">
            Week {data.current_week} · <span className="lor-generated-badge">rules-based</span>
          </div>

          {data.decisions.length === 0 ? (
            <div className="lor-intel-all-clear">
              <div className="lor-intel-all-clear-icon"><Icon emoji="✅" size={28} /></div>
              <p>No lineup decisions needed this week. Your starters all look healthy and set.</p>
            </div>
          ) : (
            <div className="lor-ss-grid">
              {data.decisions.map((d, i) => (
                <StartSitCard key={i} decision={d} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function StartSitCard({ decision }) {
  if (decision.type === 'flex_decision') {
    return (
      <div className="lor-card lor-ss-card lor-ss-card--flex">
        <div className="lor-ss-card-header">
          <span className="lor-ss-slot-badge">FLEX</span>
          <span className="lor-ss-conf-badge" data-level={decision.confidence?.toLowerCase()}>
            {decision.confidence} confidence
          </span>
        </div>

        <div className="lor-ss-comparison">
          <PlayerOption
            player={decision.option_a}
            label="SITTING"
            variant="sit"
          />
          <div className="lor-ss-vs">
            <span>vs</span>
            <div className="lor-ss-diff">+{decision.projected_differential} pts</div>
          </div>
          <PlayerOption
            player={decision.option_b}
            label="STARTING"
            variant="start"
          />
        </div>

        <div className="lor-ss-matchup-row">
          <span className="lor-ss-grade-dot" style={{ background: GRADE_COLORS[decision.matchup_grade] }} />
          <span className="lor-ss-grade-label">{GRADE_LABELS[decision.matchup_grade]} matchup</span>
          <span className="lor-ss-risk">Risk: {decision.risk}</span>
        </div>

        <div className="lor-ss-reasoning">{decision.reasoning}</div>
        <div className="lor-generated-tag">⚙️ rules-based</div>
      </div>
    );
  }

  // Injury / bye / questionable flag card
  const flagPlayer  = decision.flagged_player;
  const replacement = decision.recommended_replacement;
  const flagType    = decision.type;

  return (
    <div className={`lor-card lor-ss-card lor-ss-card--flag lor-ss-card--${flagType}`}>
      <div className="lor-ss-card-header">
        <span className={`lor-ss-flag-badge lor-ss-flag-badge--${flagType}`}>
          {flagType === 'bye' ? '🗓️ BYE' : flagType === 'out' ? '🚨 OUT' : flagType === 'doubtful' ? '⚕️ DOUBT' : '⚕️ Q'}
        </span>
        <span className="lor-ss-conf-badge" data-level={decision.confidence?.toLowerCase()}>
          {decision.confidence} confidence
        </span>
      </div>

      <div className="lor-ss-flag-row">
        <PlayerChip player={flagPlayer} variant="flag" />
        <div className="lor-ss-arrow">→</div>
        {replacement
          ? <PlayerChip player={replacement} variant="start" />
          : <div className="lor-ss-no-replace">Check waiver wire</div>
        }
      </div>

      <div className="lor-ss-matchup-row">
        <span className="lor-ss-grade-dot" style={{ background: GRADE_COLORS[decision.matchup_grade] }} />
        <span className="lor-ss-grade-label">{GRADE_LABELS[decision.matchup_grade]} matchup</span>
        <span className="lor-ss-risk">Risk: {decision.risk}</span>
      </div>

      <div className="lor-ss-reasoning">{decision.reasoning}</div>
      <div className="lor-generated-tag">⚙️ rules-based</div>
    </div>
  );
}

function PlayerOption({ player, label, variant }) {
  if (!player) return <div className="lor-ss-player-slot lor-ss-player-slot--empty">—</div>;
  return (
    <div className={`lor-ss-player-option lor-ss-player-option--${variant}`}>
      <div className="lor-ss-option-label">{label}</div>
      <div className="lor-ss-player-name">{player.full_name}</div>
      <div className="lor-ss-player-meta">{player.position} · {player.team}</div>
      <div className="lor-ss-proj-range">
        <span className="lor-ss-proj-low">{player.projected_low}</span>
        <span className="lor-ss-proj-avg">{player.projected}</span>
        <span className="lor-ss-proj-high">{player.projected_high}</span>
        <span className="lor-ss-proj-label">floor / avg / ceil</span>
      </div>
      {player.injury_status && (
        <div className="lor-ss-inj-tag">{player.injury_status}</div>
      )}
    </div>
  );
}

function PlayerChip({ player, variant }) {
  if (!player) return null;
  return (
    <div className={`lor-ss-chip lor-ss-chip--${variant}`}>
      <div className="lor-ss-chip-name">{player.full_name}</div>
      <div className="lor-ss-chip-meta">{player.position} · {player.team}</div>
      <div className="lor-ss-chip-proj">{player.projected} pts</div>
      {player.injury_status && (
        <div className="lor-ss-chip-inj">{player.injury_status}</div>
      )}
    </div>
  );
}
